import React, { useCallback } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ApiService } from "../../services/apiService";
import { Mix } from "../../types/Mix";
import { useAudioPlayer } from "../../contexts/AudioPlayerContext";
import { LoadingSkeleton } from "../ui/LoadingStates";
import {
  createMixTrack,
  formatMixType,
  getMixPath,
  getMixTypeBadgeClass,
  isLiveMix,
} from "../../utils/mixUtils";
import logoImage from "../../assets/logos/e-logo-trans-40.png";

interface LatestMixesWidgetContentProps {
  count?: number;
}

const LatestMixesWidgetContent: React.FC<LatestMixesWidgetContentProps> = ({
  count = 4,
}) => {
  const { playTrack, currentTrack, isPlaying } = useAudioPlayer();
  const { data: mixes = [], isLoading, error, refetch } = useQuery({
    queryKey: ["latest-mixes", count],
    queryFn: async (): Promise<Mix[]> => {
      const response = await ApiService.getMixesPaginated({ limit: count });
      if (!response.success || !response.data) {
        throw new Error(response.error || "Failed to load mixes");
      }
      return response.data.mixes;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const playMix = useCallback((mix: Mix) => {
    const track = createMixTrack(mix);
    if (track) playTrack(track);
  }, [playTrack]);

  if (isLoading) {
    return (
      <>
        <div className="flex items-center justify-between mb-6">
          <LoadingSkeleton height="h-8" width="w-40" />
          <LoadingSkeleton height="h-4" width="w-20" />
        </div>
        <div className="flex gap-3 sm:gap-4 overflow-hidden">
          {Array.from({ length: count }).map((_, index) => (
            <div key={index} className="w-36 sm:w-44 md:w-52 flex-shrink-0">
              <LoadingSkeleton height="h-36" width="w-full" className="aspect-square mb-2" />
              <LoadingSkeleton height="h-4" width="w-3/4" className="mb-2" />
              <LoadingSkeleton height="h-3" width="w-1/2" />
            </div>
          ))}
        </div>
      </>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-400 mb-3">Unable to load mixes.</p>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-white text-sm transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (mixes.length === 0) {
    return null;
  }

  return (
    <>
      <div className="flex justify-between items-center mb-4 sm:mb-6">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-white">Latest Mixes</h2>
        </div>
        <Link
          to="/mixes"
          className="text-red-500 hover:text-red-400 transition-colors text-sm font-medium"
        >
          View All →
        </Link>
      </div>

      <div
        className="flex gap-3 sm:gap-4 overflow-x-auto scrollbar-hide pb-2"
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          WebkitOverflowScrolling: "touch",
          touchAction: "pan-x pan-y",
        }}
      >
        {mixes.map((mix) => {
          const trackId = `mix-${mix.mix_id}`;
          const isCurrent = currentTrack?.id === trackId && isPlaying;
          const showArtist = !isLiveMix(mix) && Boolean(mix.artist_name?.trim());

          return (
            <article key={mix.mix_id} className="group w-36 flex-shrink-0 sm:w-44 md:w-52">
              <div className="relative">
                <Link to={getMixPath(mix)} className="block">
                  <div className="relative aspect-square rounded-xl sm:rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:scale-105 bg-gray-900">
                    {mix.imageUrl ? (
                      <img
                        src={mix.imageUrl}
                        alt={`${mix.title} artwork`}
                        className="absolute inset-0 w-full h-full object-cover"
                        loading="lazy"
                        onError={(event) => {
                          event.currentTarget.style.display = "none";
                        }}
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                        <img src={logoImage} alt="" className="w-12 h-12 sm:w-16 sm:h-16 opacity-70" />
                      </div>
                    )}

                    <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/80 via-black/40 to-transparent p-2 sm:p-4 z-10">
                      <img
                        src={logoImage}
                        alt="Eruption Radio"
                        className="w-5 h-5 sm:w-6 sm:h-6 opacity-90"
                      />
                    </div>

                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />

                    <div className="absolute bottom-0 left-0 right-0 p-2 sm:p-4">
                      <span
                        className={`mb-2 inline-flex rounded-full border px-2 py-0.5 text-[0.55rem] font-black tracking-widest shadow-lg sm:text-[0.6rem] ${getMixTypeBadgeClass(mix.type)}`}
                      >
                        {formatMixType(mix.type)}
                      </span>
                      <h3 className="text-white font-bold text-sm sm:text-lg leading-tight mb-0.5 sm:mb-1 line-clamp-2">
                        {mix.title}
                      </h3>
                      {showArtist ? (
                        <p className="text-gray-200 text-xs sm:text-sm line-clamp-1">
                          {mix.artist_name}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </Link>

                <button
                  onClick={() => playMix(mix)}
                  disabled={!mix.audioUrl}
                  className={`absolute top-2 right-2 sm:top-3 sm:right-3 z-20 w-9 h-9 sm:w-10 sm:h-10 rounded-full hidden sm:flex items-center justify-center shadow-lg transition-all duration-200 opacity-0 group-hover:opacity-100 hover:scale-110 ${
                    isCurrent
                      ? "bg-green-500 hover:bg-green-600"
                      : "bg-red-500 hover:bg-red-600"
                  } disabled:bg-gray-600 disabled:cursor-not-allowed`}
                  aria-label={isCurrent ? `Playing ${mix.title}` : `Play ${mix.title}`}
                >
                  {isCurrent ? (
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </>
  );
};

export default LatestMixesWidgetContent;
